/**
 * What each prop kind IS to the simulation: its footprint, how tall it is (which decides
 * whether it hides a crawler, a standing man, or nothing), and what can destroy it.
 * The renderer has its own table mapping the same kinds to models; neither imports the other.
 */

import type { PropDef, PropKind } from './types';

/** `bombOnly`: mission 5's pumping station - "so heavily armoured that only a time bomb works". */
export type Durability = 'none' | 'soft' | 'hard' | 'bombOnly';

/** A face of a prop, named in the prop's OWN frame: yaw 0 = front to the south, so its `north` is its back. */
export type Side = 'north' | 'south' | 'east' | 'west';

interface Shape {
  shape: 'circle' | 'box';
  /** circle radius, or box half-extents along x / z */
  r?: number;
  hx?: number;
  hz?: number;
  h: number;
  dx?: number;
  dz?: number;
}

export interface PropSpec {
  shapes: Shape[];
  /** `soft`: grenade, LAW or bomb. `hard`: LAW or bomb only. `bombOnly`: a time bomb, nothing else (02 §7). */
  durability: Durability;
  blocksMove: boolean;
  /** Wire and the like: stops feet, not eyes or bullets. */
  transparent?: boolean;
  /** Cook-off radius when destroyed (fuel, ammunition). */
  secondaryBlast?: number;
  /** Counts toward the emplacement score line. */
  emplacement?: boolean;
  lootable?: boolean;
  /** Length-driven footprint (walls, wire, sandbag runs): half-depth in z. */
  runHalfDepth?: number;
  /** Belongs in water (a bridge pier, a moored barge): the generator's "stands in water" gate lets it be. */
  wet?: boolean;
  /**
   * The only face a blast can open it from (10 §2, mission 19: a casemate's steel rear door). A blast that would
   * otherwise destroy it does nothing unless its centre lies on this side of the prop (`blastOpens`).
   */
  weakSide?: Side;
  /** What is on that face, upper case, as the player is told: "THE DOOR IS ON THE NORTH FACE". */
  weakPoint?: string;
  /**
   * A vehicle drives straight over it (ROUND4 §1): wire, sandbags, a hedge, crates in the road. It still
   * stops a man; it does not stop three tonnes of lorry. Everything else brings a vehicle to a stand.
   */
  crushable?: boolean;
  /**
   * Somewhere a man could change out of a dead soldier's clothes and nobody would think twice (02 §6.4):
   * an inhabited building with a door. Found in play - a body dragged to a BARRACKS did nothing, because
   * this was a hand-written list of three kinds and the largest building on the map was not one of them.
   * Ruins and fortifications are not shelter: nobody lives in them and there is nothing to step behind.
   */
  shelter?: boolean;
}

const tree = (r: number): PropSpec => ({ shapes: [{ shape: 'circle', r, h: 4 }], durability: 'none', blocksMove: true });

export const PROPS: Record<PropKind, PropSpec> = {
  pine: tree(0.4),
  broadleaf: tree(0.45),
  palm: tree(0.3),
  deadTree: tree(0.3),
  cactus: { shapes: [{ shape: 'circle', r: 0.35, h: 2.2 }], durability: 'none', blocksMove: true },
  shrub: { shapes: [{ shape: 'circle', r: 0.75, h: 0.95 }], durability: 'none', blocksMove: true , crushable: true },
  rock: { shapes: [{ shape: 'circle', r: 1.0, h: 1.05 }], durability: 'none', blocksMove: true },
  bunker: { shapes: [{ shape: 'box', hx: 2.95, hz: 2.35, h: 1.9 }], durability: 'hard', blocksMove: true, emplacement: true },
  mgNest: {
    // A horseshoe of bags, open to the north: three low boxes.
    shapes: [
      { shape: 'box', hx: 1.9, hz: 0.35, h: 0.9, dz: 1.55 },
      { shape: 'box', hx: 0.35, hz: 1.1, h: 0.9, dx: -1.6, dz: 0.4 },
      { shape: 'box', hx: 0.35, hz: 1.1, h: 0.9, dx: 1.6, dz: 0.4 },
    ],
    durability: 'soft',
    blocksMove: true,
    emplacement: true,
  },
  sandbags: { shapes: [], runHalfDepth: 0.3, durability: 'soft', blocksMove: true , crushable: true },
  hut: { shapes: [{ shape: 'box', hx: 2.3, hz: 1.9, h: 2.6 }], durability: 'soft', blocksMove: true, shelter: true },
  tent: { shapes: [{ shape: 'box', hx: 1.7, hz: 2.0, h: 1.9 }], durability: 'soft', blocksMove: true, shelter: true },
  watchtower: { shapes: [{ shape: 'box', hx: 1.35, hz: 1.35, h: 5 }], durability: 'soft', blocksMove: true },
  wall: { shapes: [], runHalfDepth: 0.35, durability: 'hard', blocksMove: true },
  wire: { shapes: [], runHalfDepth: 0.3, durability: 'soft', blocksMove: true, transparent: true , crushable: true },
  crate: { shapes: [{ shape: 'box', hx: 0.55, hz: 0.55, h: 0.85 }], durability: 'soft', blocksMove: true , crushable: true },
  barrel: { shapes: [{ shape: 'circle', r: 0.42, h: 1.0 }], durability: 'soft', blocksMove: true, secondaryBlast: 3.5 },
  fuelTank: { shapes: [{ shape: 'box', hx: 2.4, hz: 1.25, h: 2.6 }], durability: 'soft', blocksMove: true, secondaryBlast: 9 },
  radar: { shapes: [{ shape: 'circle', r: 0.95, h: 4 }], durability: 'soft', blocksMove: true },
  ammoShack: { shapes: [{ shape: 'box', hx: 2.3, hz: 1.9, h: 2.6 }], durability: 'soft', blocksMove: true, secondaryBlast: 7, lootable: true },
  magazine: { shapes: [{ shape: 'box', hx: 2.95, hz: 2.35, h: 1.9 }], durability: 'hard', blocksMove: true, secondaryBlast: 10 },
  alarmPost: { shapes: [{ shape: 'circle', r: 0.3, h: 2.2 }], durability: 'soft', blocksMove: true },

  /* ---- natural cover */
  stump: { shapes: [{ shape: 'circle', r: 0.5, h: 0.7 }], durability: 'none', blocksMove: true , crushable: true },
  snowDrift: { shapes: [{ shape: 'circle', r: 1.3, h: 0.9 }], durability: 'none', blocksMove: true , crushable: true },
  hedge: { shapes: [], runHalfDepth: 0.5, durability: 'none', blocksMove: true , crushable: true },

  /* ---- fieldworks */
  tankTrap: { shapes: [{ shape: 'circle', r: 0.7, h: 1.0 }], durability: 'none', blocksMove: true , crushable: true },
  searchlight: { shapes: [{ shape: 'circle', r: 0.4, h: 3.2 }], durability: 'soft', blocksMove: true },
  turretBunker: { shapes: [{ shape: 'circle', r: 2.2, h: 2.2 }], durability: 'hard', blocksMove: true, emplacement: true },
  mannedTurret: { shapes: [{ shape: 'circle', r: 1.4, h: 1.0 }], durability: 'soft', blocksMove: true, emplacement: true },
  proximityPost: { shapes: [{ shape: 'circle', r: 0.25, h: 1.6 }], durability: 'soft', blocksMove: true, secondaryBlast: 5 },

  /* ---- buildings */
  guardHouse: { shapes: [{ shape: 'box', hx: 1.5, hz: 1.5, h: 2.5 }], durability: 'soft', blocksMove: true, shelter: true },
  barracks: { shapes: [{ shape: 'box', hx: 5.0, hz: 2.4, h: 2.8 }], durability: 'soft', blocksMove: true, shelter: true },
  ruinHouse: { shapes: [{ shape: 'box', hx: 2.6, hz: 2.2, h: 2.2 }], durability: 'none', blocksMove: true },
  ruinWall: { shapes: [], runHalfDepth: 0.35, durability: 'none', blocksMove: true },
  commsPost: { shapes: [{ shape: 'box', hx: 2.2, hz: 1.8, h: 2.6 }], durability: 'soft', blocksMove: true, shelter: true },
  // Deep enough to take the 11 m aircraft nose to tail (mission 9 photographs it in its shed).
  hangar: { shapes: [{ shape: 'box', hx: 7.0, hz: 6.5, h: 5.0 }], durability: 'hard', blocksMove: false, shelter: true },
  prison: { shapes: [{ shape: 'box', hx: 3.2, hz: 2.6, h: 2.8 }], durability: 'soft', blocksMove: true, shelter: true },

  /* ---- mission objects */
  aircraft: { shapes: [{ shape: 'box', hx: 4.5, hz: 5.5, h: 2.4 }], durability: 'soft', blocksMove: true, secondaryBlast: 5 },
  samLauncher: { shapes: [{ shape: 'circle', r: 1.6, h: 2.6 }], durability: 'soft', blocksMove: true, secondaryBlast: 6 },
  pumpStation: { shapes: [{ shape: 'box', hx: 3.0, hz: 2.4, h: 2.6 }], durability: 'bombOnly', blocksMove: true, secondaryBlast: 8 },
  pipeline: { shapes: [], runHalfDepth: 0.45, durability: 'none', blocksMove: true },
  tigerPit: { shapes: [{ shape: 'box', hx: 1.3, hz: 1.3, h: 0.3 }], durability: 'none', blocksMove: true },
  controlsPyramid: { shapes: [{ shape: 'box', hx: 0.9, hz: 0.9, h: 1.4 }], durability: 'soft', blocksMove: true },
  // Tag Recovery: a rifle in the ground with a helmet on it. Walk-through, indestructible.
  fieldGrave: { shapes: [{ shape: 'circle', r: 0.25, h: 1.2 }], durability: 'none', blocksMove: false },
  lever: { shapes: [{ shape: 'circle', r: 0.25, h: 1.0 }], durability: 'none', blocksMove: false },
  bridge: { shapes: [], durability: 'none', blocksMove: false },
  // R4: ballast, sleepers and two rails, drawn to `length` and turned by `yaw` like the bridge deck above.
  // No footprint at all: a man walks over a railway, and so does everything else the sim asks about.
  rail: { shapes: [], durability: 'none', blocksMove: false },

  /* ---- expansion, demolition (10 §2): piers, mast, casemates and barges open to a time bomb only; guns, launchers */
  /* and the locomotive want a LAW or a bomb; wagons and the generator go to a grenade, and cook off. */
  // As tall as it is drawn: its head is tucked under the deck it carries, so it hides a crawler and no more.
  bridgePier: { shapes: [{ shape: 'box', hx: 1.2, hz: 1.2, h: 0.9 }], durability: 'bombOnly', blocksMove: true, wet: true },
  locomotive: { shapes: [{ shape: 'box', hx: 1.6, hz: 5.0, h: 3.2 }], durability: 'hard', blocksMove: true, secondaryBlast: 6 },
  fuelWagon: { shapes: [{ shape: 'box', hx: 1.5, hz: 4.0, h: 2.8 }], durability: 'soft', blocksMove: true, secondaryBlast: 9 },
  fieldGun: { shapes: [{ shape: 'circle', r: 1.5, h: 1.6 }], durability: 'hard', blocksMove: true, emplacement: true },
  gunPit: {
    // A sandbag horseshoe open to the north, as the nest; the ready ammunition stacked in it cooks off.
    shapes: [
      { shape: 'box', hx: 2.6, hz: 0.35, h: 0.9, dz: 2.4 },
      { shape: 'box', hx: 0.35, hz: 1.6, h: 0.9, dx: -2.3, dz: 0.8 },
      { shape: 'box', hx: 0.35, hz: 1.6, h: 0.9, dx: 2.3, dz: 0.8 },
    ],
    durability: 'soft', blocksMove: true, secondaryBlast: 5,
  },
  relayMast: { shapes: [{ shape: 'circle', r: 0.9, h: 12 }], durability: 'bombOnly', blocksMove: true },
  generatorHut: { shapes: [{ shape: 'box', hx: 1.8, hz: 1.5, h: 2.4 }], durability: 'soft', blocksMove: true, secondaryBlast: 5, shelter: true },
  mobileLauncher: { shapes: [{ shape: 'box', hx: 1.6, hz: 4.2, h: 3.0 }], durability: 'hard', blocksMove: true, secondaryBlast: 8 },
  casemate: {
    // Embrasure to the front; the steel door in the back wall is the only way in for a charge.
    shapes: [{ shape: 'box', hx: 3.4, hz: 3.0, h: 3.0 }],
    durability: 'bombOnly', blocksMove: true, emplacement: true, weakSide: 'north', weakPoint: 'DOOR',
  },
  // A laden steel hull: grenades and rockets only scar it. It takes a charge against the side, from the towpath.
  barge: {
    shapes: [{ shape: 'box', hx: 2.2, hz: 6.0, h: 1.6 }], durability: 'bombOnly', blocksMove: true, secondaryBlast: 8, wet: true,
  },

  /* ---- expansion, buildings and things to work. The HQ and the cell block are walk-in, as the hangar is. */
  hqBuilding: { shapes: [{ shape: 'box', hx: 5.0, hz: 3.5, h: 3.2 }], durability: 'hard', blocksMove: false, shelter: true },
  cellBlock: { shapes: [{ shape: 'box', hx: 4.0, hz: 3.0, h: 3.0 }], durability: 'hard', blocksMove: false, shelter: true },
  laboratory: { shapes: [{ shape: 'box', hx: 4.0, hz: 3.0, h: 3.0 }], durability: 'soft', blocksMove: true, shelter: true },
  safe: { shapes: [{ shape: 'box', hx: 0.5, hz: 0.5, h: 1.2 }], durability: 'hard', blocksMove: true },
  telephoneExchange: { shapes: [{ shape: 'box', hx: 1.2, hz: 0.9, h: 2.0 }], durability: 'soft', blocksMove: true, shelter: true },
  crashSite: { shapes: [{ shape: 'box', hx: 4.0, hz: 4.5, h: 1.8 }], durability: 'none', blocksMove: true },
  staffCar: { shapes: [{ shape: 'box', hx: 1.0, hz: 2.2, h: 1.6 }], durability: 'soft', blocksMove: true, secondaryBlast: 4 },

  /* ---- expansion, markers and panels: walk-through, indestructible - they only say "here". */
  breakerPanel: { shapes: [{ shape: 'box', hx: 0.6, hz: 0.3, h: 1.8 }], durability: 'none', blocksMove: false },
  overlookMarker: { shapes: [{ shape: 'circle', r: 0.3, h: 0.5 }], durability: 'none', blocksMove: false },
  observationPost: { shapes: [{ shape: 'circle', r: 0.3, h: 0.6 }], durability: 'none', blocksMove: false },
  partisanCamp: { shapes: [{ shape: 'circle', r: 1.2, h: 0.5 }], durability: 'none', blocksMove: false },
  culvert: { shapes: [{ shape: 'box', hx: 1.5, hz: 0.6, h: 0.4 }], durability: 'none', blocksMove: false },
  strikeBeacon: { shapes: [{ shape: 'circle', r: 0.3, h: 0.8 }], durability: 'none', blocksMove: false },
};

const RUN_HEIGHT: Partial<Record<PropKind, number>> = { wall: 1.9, wire: 1.2, sandbags: 0.9, hedge: 1.1, ruinWall: 1.7, pipeline: 1.0 };

/** Heights that matter: below this a standing man sees and shoots over it. */
export const SIGHT_BLOCK_HEIGHT = 1.4;
export const BULLET_BLOCK_HEIGHT = 1.2;

export interface Collider {
  propId: number;
  shape: 'circle' | 'box';
  x: number;
  z: number;
  r: number;
  hx: number;
  hz: number;
  h: number;
  blocksMove: boolean;
  blocksSight: boolean;
  blocksBullets: boolean;
  /** R4: a vehicle drives over it (`PropSpec.crushable`). Absent on the ad-hoc colliders other systems build. */
  crushable?: boolean;
}

/**
 * World-space colliders for one placed prop. Boxes stay axis-aligned: yaw PI just mirrors offsets, and a run
 * turned a quarter swaps its length and depth.
 */
export function collidersFor(prop: PropDef): Collider[] {
  const spec = PROPS[prop.kind];
  const flip = Math.abs(prop.yaw) > 1.5 ? -1 : 1;
  const scale = prop.scale ?? 1;
  // ROUND7: a run laid a quarter turn round (a compound's east and west faces) runs north-south, so its box does
  // too. A box cannot turn, but a quarter turn is only a swap of its half-extents; without the swap every column
  // piece was 1.1 m thick and 0.7 m long, and the 0.4 m slits between them let eyes and bullets through a wall
  // that is drawn solid.
  const long = (prop.length ?? 4) / 2;
  const across = quarterTurned(prop.yaw);
  const shapes: Shape[] = spec.runHalfDepth !== undefined
    ? [{
      shape: 'box', hx: across ? spec.runHalfDepth : long, hz: across ? long : spec.runHalfDepth,
      h: RUN_HEIGHT[prop.kind] ?? 1,
    }]
    : spec.shapes;
  return shapes.map((s) => {
    const h = s.h * (s.shape === 'circle' ? scale : 1);
    const solid = spec.transparent !== true;
    return {
      propId: prop.id,
      shape: s.shape,
      x: prop.x + (s.dx ?? 0) * flip,
      z: prop.z + (s.dz ?? 0) * flip,
      r: (s.r ?? 0) * scale,
      hx: s.hx ?? 0,
      hz: s.hz ?? 0,
      h,
      blocksMove: spec.blocksMove,
      blocksSight: solid,
      blocksBullets: solid && h >= BULLET_BLOCK_HEIGHT,
      crushable: spec.crushable === true,
    };
  });
}

/** Is this yaw an odd number of quarter turns - a run laid north-south? Its box's extents are then swapped. */
export function quarterTurned(yaw: number): boolean {
  return Math.abs(Math.round(yaw / (Math.PI / 2))) % 2 === 1;
}

/** Going round from the back: each quarter turn of yaw carries a face on to the next of these. */
const SIDE_RING: readonly Side[] = ['north', 'west', 'south', 'east'];

/** The map side a prop-frame `side` faces once the prop is turned: yaw is read in quarter turns (0 = front south). */
export function worldSide(side: Side, yaw: number): Side {
  const quarter = ((Math.round(yaw / (Math.PI / 2)) % 4) + 4) % 4;
  return SIDE_RING[(SIDE_RING.indexOf(side) + quarter) % 4] ?? side;
}

/** Which side of a prop a point lies on: the wedge between the diagonals of its footprint (colliders never turn). */
export function sideOfProp(prop: PropDef, x: number, z: number): Side {
  let hx = 0;
  let hz = 0;
  for (const c of collidersFor(prop)) {
    hx = Math.max(hx, Math.abs(c.x - prop.x) + (c.shape === 'circle' ? c.r : c.hx));
    hz = Math.max(hz, Math.abs(c.z - prop.z) + (c.shape === 'circle' ? c.r : c.hz));
  }
  const dx = (x - prop.x) / (hx > 0 ? hx : 1);
  const dz = (z - prop.z) / (hz > 0 ? hz : 1);
  if (Math.abs(dx) > Math.abs(dz)) return dx > 0 ? 'east' : 'west';
  return dz > 0 ? 'south' : 'north';
}

/** The map side a prop can be opened from, or null when any side will do. */
export function weakSideOf(prop: PropDef): Side | null {
  const side = PROPS[prop.kind].weakSide;
  return side === undefined ? null : worldSide(side, prop.yaw);
}

/** Would a blast centred here open this prop? Always, unless it has a weak side and this is not it. */
export function blastOpens(prop: PropDef, x: number, z: number): boolean {
  const side = weakSideOf(prop);
  return side === null || sideOfProp(prop, x, z) === side;
}
